import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress } from '@mui/material';
import MapPatner from './MapPatnerClient';
import { useNavigate } from 'react-router-dom';
import { getAddressFromCoords } from './getAddressFromCoords';
import { formatInTimeZone } from 'date-fns-tz';

interface Chamado {
  id?: number;
  cliente_id: number;
  localizacao: string;
  endereco_textual: string;
  status: string;
}

interface ChamadoModalProps {
  open: boolean;
  onClose: () => void;
}

const ChamadoModalClient: React.FC<ChamadoModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [chamado, setChamado] = useState<Chamado | null>(null);
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMounted = React.useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const tentarBuscarEndereco = useCallback(async (lat: number, lon: number, tentativas = 5, delayMs = 1500) => {
    for (let i = 0; i < tentativas; i++) {
      if (!isMounted.current) return null;
      try {
        const endereco = await getAddressFromCoords(lat, lon);
        if (endereco && endereco.trim() !== '') return endereco;
      } catch (e) {
      }
      if (i < tentativas - 1) {
        await new Promise(res => setTimeout(res, delayMs));
      }
    }
    return null;
  }, []);

  const obterLocalizacaoAtual = useCallback(async () => {
    setError(null);
    if (!navigator.geolocation) {
      setError('Seu dispositivo não suporta geolocalização.');
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000 // Aumentado para 10s para dar mais tempo
        });
      });

      if (!isMounted.current) return;

      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      const localizacao = `${latitude},${longitude}`;
      
      // Define o estado inicial com a localização, mas sem o endereço ainda
      setChamado({
        cliente_id: Number(localStorage.getItem('clienteId')),
        localizacao,
        endereco_textual: 'Buscando endereço...',
        status: 'Pendente'
      });

      // Busca o endereço em segundo plano
      tentarBuscarEndereco(latitude, longitude).then((endereco) => {
        if (!isMounted.current) return;
        setChamado(prev => {
          if (!prev) return null;
          return {
            ...prev,
            endereco_textual: endereco || 'Endereço não encontrado'
          };
        });
      });

    } catch (error: any) {
      console.error('Erro de geolocalização:', error);
      if (isMounted.current) {
        let msg = 'Erro ao obter localização.';
        if (error.code === 1) msg = 'Permissão de localização negada.';
        else if (error.code === 2) msg = 'Localização indisponível.';
        else if (error.code === 3) msg = 'Tempo limite esgotado ao buscar localização.';
        setError(msg);
      }
    }
  }, [tentarBuscarEndereco]);

  useEffect(() => {
    if (open) {
      setChamado(null);
      setError(null);
      setMapLoading(true);
      obterLocalizacaoAtual().finally(() => {
        if (isMounted.current) {
          setMapLoading(false);
        }
      });
    }
  }, [open, obterLocalizacaoAtual]);

  const handleFazerAtendimento = async () => {
    if (!chamado) return;
    
    setLoading(true);
    const url = process.env.REACT_APP_SUPABASE_URL;
    const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
    
    if (!url || !serviceKey) {
      console.error('Supabase URL ou Service Key não definidos nas variáveis de ambiente.');
      setLoading(false);
      return;
    }

    const headers: HeadersInit = {
      'apikey': serviceKey,
      'Authorization': `Bearer ${serviceKey}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal'
    };

    // Adiciona data_abertura no momento do envio
    const now = new Date();
    const dataAbertura = formatInTimeZone(now, 'America/Sao_Paulo', "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
    const chamadoCompleto = { ...chamado, data_abertura: dataAbertura };

    try {
      const response = await fetch(`${url}/rest/v1/chamado`, {
        method: 'POST',
        headers,
        body: JSON.stringify(chamadoCompleto)
      });

      if (!response.ok) {
        throw new Error('Erro ao criar chamado');
      }

      // Obter o id do chamado criado
      const locationHeader = response.headers.get('Location');
      let chamadoId = null;
      if (locationHeader) {
        const match = locationHeader.match(/id=eq\.(\d+)/);
        if (match) {
          chamadoId = Number(match[1]);
        }
      }

      // Se não conseguir pelo header, buscar o último chamado do cliente
      if (!chamadoId) {
        const res = await fetch(
          `${url}/rest/v1/chamado?select=id&cliente_id=eq.${chamado.cliente_id}&order=id.desc&limit=1`, 
          { headers }
        );
        const data = await res.json();
        chamadoId = data && data[0] && data[0].id ? data[0].id : null;
      }

      // Armazenar o ID do chamado no localStorage
      if (chamadoId) {
        localStorage.setItem('chamadoId', chamadoId.toString());
        localStorage.setItem('clienteId', chamado.cliente_id.toString());
        // Criar log_chamado
        const logData = {
          chamado_id: chamadoId,
          cliente_id: chamado.cliente_id,
          localizacao: chamado.localizacao,
          endereco_textual: chamado.endereco_textual,
          status: 'Pendente',
          data_abertura: new Date(new Date().getTime() - (3 * 60 * 60 * 1000)).toISOString()
        };

        await fetch(`${url}/rest/v1/log_chamado`, {
          method: 'POST',
          headers,
          body: JSON.stringify(logData)
        });

        navigate('/emergency');
      }
    } catch (error) {
      console.error('Erro ao criar chamado:', error);
      alert('Erro ao criar chamado. Por favor, tente novamente.');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '600px !important',
          maxHeight: '100vh',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle>Localização do Atendimento</DialogTitle>
      <DialogContent sx={{ p: 2, pb: 0, position: 'relative', overflow: 'hidden' }}>
        <Box
          component="img"
          src={require('../../assets/Design sem nome (7).png')}
          alt="Marca d'água Support Life"
          sx={{
            position: 'absolute',
            top: '-120px',
            left: '50%',
            width: '100%',
            height: '360px',
            objectFit: 'contain',
            opacity: 0.07,
            pointerEvents: 'none',
            zIndex: 0,
            transform: 'translateX(-50%)',
          }}
        />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, position: 'relative', zIndex: 1, mt: 0 }}>
          <Typography sx={{ color: 'text.primary', fontSize: 15, mb: 1 }}>
            O atendimento usará sua localização atual.
          </Typography>
          <Box>
            <Box sx={{ mt: 2, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
              {mapLoading ? (
                <CircularProgress size={40} color="primary" />
              ) : error ? (
                <>
                  <Typography color="error" align="center" sx={{ px: 2 }}>
                    {error}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => {
                      setMapLoading(true);
                      obterLocalizacaoAtual().finally(() => setMapLoading(false));
                    }}
                  >
                    Tentar Novamente
                  </Button>
                </>
              ) : !chamado?.localizacao ? (
                 <Typography color="text.secondary">Aguardando localização...</Typography>
              ) : (
                <MapPatner
                  center={{
                    lat: Number(chamado.localizacao.split(',')[0]),
                    lng: Number(chamado.localizacao.split(',')[1])
                  }}
                  zoom={15}
                  markers={[
                    {
                      position: {
                        lat: Number(chamado.localizacao.split(',')[0]),
                        lng: Number(chamado.localizacao.split(',')[1])
                      },
                      title: 'Local do chamado'
                    }
                  ]}
              />
              )}
            </Box>
          </Box>
          {!!chamado?.endereco_textual && (
            <Typography 
              sx={{ 
                fontSize: 15, 
                color: 'text.secondary'
              }}
            >
              <strong>Endereço:</strong> {chamado?.endereco_textual}
            </Typography>
          )}
          </Box>
      </DialogContent>
      <DialogActions sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        width: '100%',
        justifyContent: 'space-between',
        pb: 2,
        pt: 3,
        px: 3
      }}>
        <Button
          variant="contained"
          color="primary"
          sx={{ minWidth: 120, fontWeight: 600 }}
          fullWidth
          onClick={handleFazerAtendimento}
          disabled={loading || !chamado?.localizacao}
        >
          {loading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Processando...
            </>
          ) : (
            'Continuar'
          )}
        </Button>
        <Button
          variant="outlined"
          color="error"
          sx={{ minWidth: 120, fontWeight: 600 }}
          fullWidth
          onClick={onClose}
          disabled={loading}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChamadoModalClient; 
